import Button from "components/button";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FiDownload } from "react-icons/fi";
import { showToast } from "components/toast";

/* ------------ Small helpers (simple & readable) ------------ */
const show = (v) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

const toIST = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
};

/* ------------ Format number: round to 2 decimals if needed ------------ */
const formatNumber = (num) => {
  if (num === null || num === undefined) return "—";
  return Number(num) % 1 === 0 ? String(num) : Number(num).toFixed(2);
};

/* ------------ Draw the header/meta block (Container) ------------ */
function drawContainerHeader(doc, container) {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(51, 17, 219);
  doc.rect(0, 0, pageWidth, 15, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont(undefined, "bold");
  doc.text("CONTAINER REPORT", pageWidth / 2, 10, { align: "center" });

  const contentY = 20;
  doc.setTextColor(0, 0, 0);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(10, contentY, pageWidth - 20, 25, 3, 3, "F");
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.roundedRect(10, contentY, pageWidth - 20, 25, 3, 3, "S");

  const leftX = 20;
  const rightX = Math.max(leftX + 90, pageWidth / 2 + 10);
  const startY = contentY + 7;
  const gapY = 6;

  const left = [
    ["Container No", show(container?.containerNo)],
    ["Departure Port", show(container?.departurePort)],
    ["Arrival Port", show(container?.arrivalPort)],
  ];

  const right = [
    ["Status", show(container?.status?.name)],
    ["Departure Date", show(container?.departureTimeDate)],
    ["ETA", show(container?.eta)],
  ];

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);

  left.forEach(([label, value], i) => {
    const y = startY + i * gapY;
    doc.text(`${label}:`, leftX, y);
    doc.setTextColor(0, 0, 0);
    doc.text(String(value), leftX + 35, y);
    doc.setTextColor(100, 100, 100);
  });

  right.forEach(([label, value], i) => {
    const y = startY + i * gapY;
    doc.text(`${label}:`, rightX, y);
    doc.setTextColor(0, 0, 0);
    doc.text(String(value), rightX + 35, y);
    doc.setTextColor(100, 100, 100);
  });

  return contentY + 30;
}

/* ------------ Gather items robustly ------------ */
function getContainerItems(container) {
  if (Array.isArray(container?.items)) return container.items;
  if (Array.isArray(container?.containerItems)) return container.containerItems;
  if (Array.isArray(container?.data)) return container.data;
  return [];
}

/* ------------ Map row for the table ------------ */
function mapItemRow(item) {
  return [
    show(item?.name),
    show(item?.marka),
    show(item?.description),
    formatNumber(item?.ctn),
    formatNumber(item?.qty),
    formatNumber(item?.kg),
    formatNumber(item?.cbm),
    formatNumber(item?.rate),
    formatNumber(item?.totalAmount),
    formatNumber(item?.totalQty),
    formatNumber(item?.totalKg),
    formatNumber(item?.totalCBM),
  ];
}

/* ------------ Export Button ------------ */
const ExportPdfButton = () => {
  const {
    containerDetailsData,
    containerDetailsLoading,
    containerDetailsError,
  } = useSelector((state) => state.containers);

  const handleExport = () => {
    if (containerDetailsError)
      return showToast.error("❌ Cannot export: container details error");
    if (!containerDetailsData)
      return showToast.error("⚠️ No container details to export");

    const items = getContainerItems(containerDetailsData);
    if (!items?.length)
      return showToast.error("⚠️ No container items to export");

    const doc = new jsPDF({ orientation: "landscape" });

    const pageCountHook = (data) => {
      const str = `Page ${data.pageNumber} of ${doc.getNumberOfPages()}`;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(str, pageWidth - 12, pageHeight - 6, { align: "right" });
    };

    const startY = drawContainerHeader(doc, containerDetailsData);

    // Grand totals
    const grandTotals = items.reduce(
      (totals, item) => {
        totals.ctn += Number(item?.ctn ?? 0);
        totals.qty += Number(item?.qty ?? 0);
        totals.kg += Number(item?.kg ?? 0);
        totals.cbm += Number(item?.cbm ?? 0);
        totals.rate += Number(item?.rate ?? 0);
        totals.totalAmount += Number(item?.totalAmount ?? 0);
        totals.totalQty += Number(item?.totalQty ?? 0);
        totals.totalKg += Number(item?.totalKg ?? 0);
        totals.totalCBM += Number(item?.totalCBM ?? 0);
        return totals;
      },
      {
        ctn: 0,
        qty: 0,
        kg: 0,
        cbm: 0,
        rate: 0,
        totalAmount: 0,
        totalQty: 0,
        totalKg: 0,
        totalCBM: 0,
      }
    );

    const grandTotalRow = [
      "GRAND TOTAL",
      "",
      "",
      formatNumber(grandTotals.ctn),
      formatNumber(grandTotals.qty),
      formatNumber(grandTotals.kg),
      formatNumber(grandTotals.cbm),
      formatNumber(grandTotals.rate),
      formatNumber(grandTotals.totalAmount),
      formatNumber(grandTotals.totalQty),
      formatNumber(grandTotals.totalKg),
      formatNumber(grandTotals.totalCBM),
    ];

    autoTable(doc, {
      head: [
        [
          "ITEM NAME",
          "MARKA",
          "DESCRIPTION",
          "CTN",
          "QTY/CTN",
          "KG/CTN",
          "CBM/CTN",
          "RATE",
          "TOTAL AMOUNT",
          "TOTAL QTY",
          "TOTAL KG",
          "TOTAL CBM",
        ],
      ],
      body: [...items.map(mapItemRow), grandTotalRow],
      startY,
      styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fillColor: [51, 17, 219], textColor: 255 },
      didDrawPage: pageCountHook,
      didParseCell: (data) => {
        if (data.row.index === items.length) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [230, 230, 230];
        }
      },
    });

    const fileName = `Container-${show(
      containerDetailsData?.containerNo || "report"
    )}.pdf`;
    doc.save(fileName);
  };

  return (
    <Button onClick={handleExport} disabled={containerDetailsLoading}>
      <FiDownload className="mr-2 inline-block" />
      Export
    </Button>
  );
};

export default ExportPdfButton;
